import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import articleTranslationsQuery
  from '../../../lib/queries/news/articleTranslations.graphql';
import withQuery from '../../../lib/withQuery';
import { useSiteContext } from '../../SiteContext';
import messages from '../../../lib/messages.json';
import LinkAndAnchor from '../../LinkAndAnchor';
import {
  getTranslationsByLanguages,
  getOriginalArticle,
  getArticleUrl,
  Locale,
  NewsArticleUrlFields,
} from '../../../lib/selectTranslatedArticleLinks';
import {
  NewsArticleTranslationsQuery,
} from '../../../lib/graphql/api/graphql';

const TranslationLink = ({
  locale,
  article,
}: {
  locale: Locale;
  article: NewsArticleUrlFields;
}) => {
  const { origin } = useSiteContext();

  return (
    <>
      {' '}
      <LinkAndAnchor
        useRouter={false}
        href={getArticleUrl(article, origin, locale)}
      >
        { locale.slice(0, 2) }
      </LinkAndAnchor>
      {' '}
    </>
  );
};

interface TranslationLinksProperties {
  data: NewsArticleTranslationsQuery;
}

export const TranslationLinks = ({
  data,
}: TranslationLinksProperties) => {
  const intl = useIntl();
  const { locale } = useSiteContext();
  const translationsByLanguage = getTranslationsByLanguages(
    data,
    locale as Locale,
  );

  const translationLanguages = Object.keys(translationsByLanguage);
  const originalArticle = getOriginalArticle(data);

  return (
    <>
      { originalArticle?.newsArticle && (
        <>
          <span>{ intl.formatMessage(messages.readTheOriginal) }</span>
          <TranslationLink
            key={originalArticle.locale}
            locale={originalArticle.locale}
            article={originalArticle.newsArticle}
          />
        </>
      )}
      { originalArticle && translationLanguages.length > 0 && (
      <span>{ intl.formatMessage(messages.or) }</span>
      )}
      { translationLanguages.length > 0 && translationLanguages.map(
        (translationKey) => {
          const {
            locale: translationLocale,
            newsArticle,
          } = translationsByLanguage[translationKey];

          if (!translationLocale) {
            return null;
          }

          return (
            <TranslationLink
              key={translationLocale}
              locale={translationLocale}
              article={newsArticle}
            />
          );
        },
      )}
    </>
  );
};

const TranslationLinksWithQuery = withQuery(TranslationLinks);

const errorMessage = '';

const translationLinksWithDataPropertyTypes = {
  article: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

type TranslationLinksWithDataProperties = InferProps<
  typeof translationLinksWithDataPropertyTypes
>;

const TranslationLinksWithData = ({
  article,
}: TranslationLinksWithDataProperties) => {
  const { newsboard, hasLocalNews } = useSiteContext();
  const { id } = article;

  return (
    <TranslationLinksWithQuery
      query={articleTranslationsQuery}
      fetchPolicy="no-cache"
      variables={{
        duplicatesOf: {
          id,
          newsboard,
          local: hasLocalNews,
        },
      }}
      errorMessage={errorMessage}
      loader={<HiddenLoader />}
    />
  );
};

TranslationLinksWithData.propTypes = translationLinksWithDataPropertyTypes;

export default TranslationLinksWithData;
